import { hrefOf } from '../overview/href'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import {
  performanceManagementLiveQuery,
  performanceManagementQuery,
  performanceManagementWatch,
  performanceSeriesQuery,
  performanceTopQuery,
  performanceTrendQuery,
  type DevicePerformanceManagementPage,
  type PerformanceChartMeta,
  type PerformanceHistory,
  type PerformanceRamMix,
  type PerformanceServiceCard,
  type PerformanceSplitSeries,
  type PerformanceTrend,
} from '../api/devices'
import { Spinner } from '../ui/Spinner'
import { NotYetMoved } from './bridge'
import { LiveCommunications, LiveConnections, LiveDiskspace, LiveDomain } from './DevicePerformanceLive'
import type { ScreenProps } from './screens'

/**
 * Fila Performance Management - performanceStatus.php (banda) +
 * performanceSummary.php (cadrane, trend, cele 30 de zile, graficele).
 */

const ARC_LENGTH = 400.55
const RANGES: { key: string; label: string }[] = [
  { key: '24h', label: '24 h' },
  { key: '7d', label: '7 d' },
  { key: '30d', label: '30 d' },
  { key: '90d', label: '90 d' },
  { key: '1y', label: '1 y' },
]
const TIER_NAME: Record<string, string> = {
  minute: 'every minute',
  five: 'every 5 minutes',
  hour: 'hourly average and peak',
  day: 'daily average and peak',
}

function formatCount(n: number): string {
  return Math.round(Number(n) || 0).toLocaleString()
}

function formatLoad(n: number): string {
  const v = Number(n) || 0
  return v.toFixed(v >= 10 ? 0 : 1)
}

function formatBytes(n: number): string {
  let v = Number(n) || 0
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0
  while (v >= 1024 && i < units.length - 1) {
    v = v / 1024
    i++
  }
  return `${i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`
}

function formatNow(kind: string, value: number): string {
  if (kind === 'bytes') return formatBytes(value)
  if (kind === 'load') return formatLoad(value)
  return formatCount(value)
}

function dialColour(percent: number): string {
  if (percent >= 90) return '#e34c4c'
  if (percent >= 75) return '#f0ad4e'
  return '#36a3d9'
}

function dash(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  return String(value)
}

export function DevicePerformanceManagement({ id, open }: ScreenProps) {
  const { data, error } = useQuery(performanceManagementQuery(id))
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />
  return <Board page={data} open={open} />
}

function Board({ page, open }: { page: DevicePerformanceManagementPage; open: (href: string) => void }) {
  const first = page.tabs[0]?.key ?? 'summary'
  const [active, setActive] = useState(first)
  const current = page.tabs.find((t) => t.key === active) ?? page.tabs[0]

  return (
    <div className="row">
      <div className="col-md-12">
        <ul className="nav nav-tabs line-tabs mb-2 managementList" role="tablist">
          {page.tabs.map((tab) => (
            <li key={tab.key} className={tab.key === active ? 'active' : undefined}>
              <a
                href={hrefOf(`${tab.href}${page.device.id}`) ?? '#'}
                onClick={(e) => {
                  e.preventDefault()
                  setActive(tab.key)
                }}
              >
                <i className={tab.icon} aria-hidden="true"></i> {tab.label}
              </a>
            </li>
          ))}
        </ul>
        <div className="tab-content">
          {current?.key === 'summary' ? (
            <Summary page={page} open={open} onTab={setActive} />
          ) : current?.key === 'liveConnections' ? (
            <LiveConnections id={page.device.id} />
          ) : current?.key === 'liveCommunications' ? (
            <LiveCommunications id={page.device.id} />
          ) : current?.key === 'liveDiskspace' ? (
            <LiveDiskspace id={page.device.id} />
          ) : current?.key === 'liveDomain' ? (
            <LiveDomain id={page.device.id} />
          ) : current ? (
            <NotYetMoved id={page.device.id} href={current.href} label={current.label} />
          ) : null}
        </div>
      </div>
    </div>
  )
}

function Summary({
  page,
  open,
  onTab,
}: {
  page: DevicePerformanceManagementPage
  open: (href: string) => void
  onTab: (key: string) => void
}) {
  const live = useQuery({
    ...performanceManagementLiveQuery(page.device.id),
    enabled: true,
  })
  const top = useQuery(performanceTopQuery(page.device.id))
  useEffect(() => {
    void performanceManagementWatch(page.device.id)
    const timer = window.setInterval(() => {
      void performanceManagementWatch(page.device.id)
    }, 8000)
    return () => window.clearInterval(timer)
  }, [page.device.id])
  const data = live.data ?? page.live

  return (
    <>
      <div className="pm-board">
        {page.service && <ServiceCard service={page.service} />}
        <DialCard
          keyName="cpu"
          title="Processor"
          percent={Number(data.cpu_util ?? 0)}
          fresh={freshLabel(data.age_seconds)}
          rows={[
            { k: 'Speed limit', id: 'pm-cpu-speed', bar: true, value: speedText(data.cpu_speed_limit, data.cpu_throttle_reason), limit: data.cpu_speed_limit },
            { k: 'User / system', id: 'pm-cpu-user-system', value: data.cpu_user === null || data.cpu_user === undefined ? null : `${Number(data.cpu_user || 0).toFixed(1)}% / ${Number(data.cpu_system || 0).toFixed(1)}%` },
            { k: 'I/O wait', id: 'pm-cpu-iowait', value: data.cpu_iowait === null || data.cpu_iowait === undefined ? null : `${Number(data.cpu_iowait || 0).toFixed(1)}%` },
            { k: 'Steal', id: 'pm-cpu-steal', value: data.cpu_steal === null || data.cpu_steal === undefined ? null : `${Number(data.cpu_steal || 0).toFixed(1)}%` },
            { k: 'Load 1 min', id: 'pm-cpu-load', value: data.cpu_load1 === null || data.cpu_load1 === undefined ? null : `${Number(data.cpu_load1 || 0).toFixed(2)} / ${data.cpu_count || 0}` },
            { k: 'Pressure', id: 'pm-cpu-psi', value: data.cpu_psi === null || data.cpu_psi === undefined ? null : Number(data.cpu_psi) > 0 ? Number(data.cpu_psi).toFixed(2) : 'none' },
          ]}
        />
        <DialCard
          keyName="ram"
          title="Memory"
          percent={Number(data.ram_used_percent ?? 0)}
          fresh={freshLabel(data.age_seconds)}
          mix={data.ram_mix ?? null}
          rows={[
            { k: 'In use', id: 'pm-ram-mix', mix: true },
            { k: 'Available', id: 'pm-ram-available', value: data.ram_available ?? null },
            { k: 'Swap used', id: 'pm-ram-swap', value: `${Number(data.ram_swap_percent || 0).toFixed(1)}%` },
            { k: 'Swap size', id: 'pm-ram-swap-size', value: `${data.ram_swap_used || ''} / ${data.ram_swap_total || ''}` },
            { k: 'Pressure', id: 'pm-ram-psi', value: data.ram_psi === null || data.ram_psi === undefined ? null : Number(data.ram_psi) > 0 ? Number(data.ram_psi).toFixed(2) : 'none' },
            { k: 'Running apps', id: 'pm-running-apps', value: data.running_applications ?? null },
          ]}
        />
      </div>
      <TrendCard id={page.device.id} metrics={page.trend_metrics} />
      <HistoryCard history={page.history} />
      <div className="row">
        {page.charts.map((chart) => (
          <div className="col-md-4" key={chart.key}>
            <LineCard chart={chart} id={page.device.id} top={top.data?.[chart.top]} onOpen={() => onTab(chart.tab)} />
          </div>
        ))}
        <div className="col-md-4">
          <SplitCard
            id={page.device.id}
            kind="discio"
            title="Disc I/O"
            color="#e67e22"
            nowA="disc-read"
            nowB="disc-write"
            labels={['read', 'write']}
            top={top.data?.io}
            onOpen={() => onTab('discIO')}
          />
        </div>
        <div className="col-md-4">
          <SplitCard
            id={page.device.id}
            kind="network"
            title="Network"
            color="#2ecc71"
            nowA="net-rx"
            nowB="net-tx"
            labels={['in', 'out']}
            onOpen={() => open('opteemum/networkStatus/')}
          />
        </div>
        <div className="col-md-4">
          <div className="pm-chart-card" data-tab="processLoad" onClick={() => onTab('processLoad')}>
            <div className="pm-chart-head">
              <strong>Disk</strong>
              <span className="pm-open">busy &amp; wait</span>
            </div>
            <div className="pm-chart-now">
              <span id="pm-disk-util">{data.disk_util === null || data.disk_util === undefined ? '—' : `${Number(data.disk_util).toFixed(1)}%`}</span>
            </div>
            <div style={{ paddingBottom: 8 }}>
              <div className="pm-readout-row">
                <span className="pm-k">Await</span>
                <span className="pm-v" id="pm-disk-await">
                  {data.disk_await_ms === null || data.disk_await_ms === undefined ? '—' : `${Number(data.disk_await_ms).toFixed(1)} ms`}
                </span>
              </div>
              <div className="pm-readout-row">
                <span className="pm-k">Read / write</span>
                <span className="pm-v" id="pm-disk-rw">
                  {data.disk_read_bps === null || data.disk_read_bps === undefined ? '—' : `${data.disk_read_bps || ''} / ${data.disk_write_bps || ''}`}
                </span>
              </div>
              <div className="pm-readout-row">
                <span className="pm-k">Connections</span>
                <span className="pm-v" id="pm-net-total">
                  {dash(data.net_connections)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function freshLabel(age: number | null | undefined): string {
  if (typeof age !== 'number') return ''
  return age < 60 ? `· ${age}s ago` : `· ${Math.round(age / 60)}m ago`
}

function speedText(limit: number | null | undefined, reason: string | undefined): string | null {
  if (limit === null || limit === undefined || !(Number(limit) > 0)) return null
  const n = Math.max(1, Math.min(100, Number(limit)))
  if (n >= 100) return `${n}% · full speed`
  return `${n}% · ${reason || 'throttled'}`
}

function ServiceCard({ service }: { service: PerformanceServiceCard }) {
  return (
    <div className="pm-dial-card pm-service-card">
      <div className={`pm-dial pm-service-emblem ${service.tone}`}>
        <i className="fas fa-server"></i>
        <div className="pm-service-state">{service.status_label}</div>
      </div>
      <div className="pm-readout">
        <div className="pm-dial-title">
          Service
          <span className="pm-fresh">{service.role_label}</span>
          {service.others.map((extra) => (
            <span key={extra} className="pm-fresh" style={{ fontWeight: 400, opacity: 0.75 }}>
              {extra}
            </span>
          ))}
        </div>
        {service.kpi && (
          <div className="pm-readout-row">
            <span className="pm-k">{service.kpi.label}</span>
            <span className="pm-v">{service.kpi.display}</span>
          </div>
        )}
        {service.runs && (
          <div className="pm-readout-row">
            <span className="pm-k">Runs</span>
            <span className="pm-v">{service.runs}</span>
          </div>
        )}
        {service.version && (
          <div className="pm-readout-row">
            <span className="pm-k">Version</span>
            <span className="pm-v" title={service.version_title}>
              {service.version}
            </span>
          </div>
        )}
        {service.signals.map((signal) => (
          <div className="pm-readout-row" key={signal.label}>
            <span className="pm-k">{signal.label}</span>
            <span className="pm-v">{signal.display}</span>
          </div>
        ))}
        {service.next && (
          <div className="pm-readout-row">
            <span className="pm-k">Next</span>
            <span className="pm-v pm-next">{service.next}</span>
          </div>
        )}
      </div>
    </div>
  )
}

function DialCard({
  keyName,
  title,
  percent,
  fresh,
  rows,
  mix,
}: {
  keyName: string
  title: string
  percent: number
  fresh: string
  mix?: PerformanceRamMix | null
  rows: { k: string; id: string; value?: string | number | null; bar?: boolean; mix?: boolean; limit?: number | null }[]
}) {
  const pct = Math.max(0, Math.min(100, Number(percent) || 0))
  const offset = ARC_LENGTH * (1 - pct / 100)
  const rotate = -135 + pct * 2.7
  return (
    <div className="pm-dial-card">
      <div className="pm-dial">
        <svg viewBox="0 0 200 170" role="img" aria-label={`${title} usage`}>
          <path className="pm-arc-bg" fill="none" strokeWidth="13" strokeLinecap="round" d="M 39.90 155.10 A 85 85 0 1 1 160.10 155.10" />
          <path
            className="pm-arc-fg"
            data-arc={keyName}
            fill="none"
            strokeWidth="13"
            strokeLinecap="round"
            stroke={dialColour(pct)}
            d="M 39.90 155.10 A 85 85 0 1 1 160.10 155.10"
            strokeDasharray="400.55"
            strokeDashoffset={String(offset)}
          />
          <line
            className="pm-needle"
            data-needle={keyName}
            x1="100"
            y1="95"
            x2="100"
            y2="35"
            stroke="#34495e"
            strokeWidth="3"
            strokeLinecap="round"
            transform={`rotate(${rotate} 100 95)`}
          />
          <circle cx="100" cy="95" r="6" fill="#34495e" />
          <text className="pm-dial-value" data-dial-value={keyName} x="100" y="126" textAnchor="middle">
            {pct.toFixed(1)}%
          </text>
          <text className="pm-dial-unit" x="100" y="145" textAnchor="middle">
            {title}
          </text>
        </svg>
      </div>
      <div className="pm-readout">
        <div className="pm-dial-title">
          {title}
          <span className="pm-fresh" data-fresh={keyName}>
            {fresh}
          </span>
        </div>
        {rows.map((row) =>
          row.bar ? (
            <SpeedBar key={row.id} row={row} />
          ) : row.mix ? (
            <MemoryMix key={row.id} mix={mix ?? null} />
          ) : (
            <div className="pm-readout-row" key={row.id}>
              <span className="pm-k">{row.k}</span>
              <span className="pm-v" id={row.id}>
                {dash(row.value)}
              </span>
            </div>
          ),
        )}
      </div>
    </div>
  )
}

function SpeedBar({ row }: { row: { id: string; value?: string | number | null; limit?: number | null } }) {
  const limit = row.limit
  const unknown = limit === null || limit === undefined || !(Number(limit) > 0)
  const n = unknown ? 100 : Math.max(1, Math.min(100, Number(limit)))
  const cls = unknown ? 'is-unknown' : n < 75 ? 'is-crit' : n < 100 ? 'is-warn' : ''
  return (
    <div
      className={`pm-readout-row pm-bar-row ${cls}`}
      id={`${row.id}-row`}
      title="How much of its rated speed the processor is allowed right now (thermal, power budget or power policy)"
    >
      <span className="pm-k">Speed limit</span>
      <span className="pm-bar">
        <span className="pm-bar-fill" id={`${row.id}-fill`} style={{ width: `${n}%` }}></span>
      </span>
      <span className="pm-v" id={row.id}>
        {dash(row.value)}
      </span>
    </div>
  )
}

function MemoryMix({ mix }: { mix: PerformanceRamMix | null }) {
  const on = !!(mix && Number(mix.total) > 0)
  const parts = ['wired', 'app', 'compressed', 'cached', 'free'] as const
  return (
    <div
      className={`pm-readout-row pm-mix-row${on ? ' is-on' : ''}`}
      id="pm-ram-mix-row"
      title="What the used percentage is made of. Wired: kernel and drivers, never paged out. App: application memory. Compressed: application data held compressed in RAM. Cached: files kept in RAM, released on demand - not counted as used."
    >
      <div className="pm-mix-bar" id="pm-ram-mix-bar">
        {parts.map((k) => {
          const v = on ? Number(mix![k]) || 0 : 0
          const pct = on ? Math.max(0, Math.min(100, (v * 100) / mix!.total)) : 0
          return <span key={k} className={`mx-${k}`} style={{ width: `${pct.toFixed(2)}%` }}></span>
        })}
      </div>
      <div className="pm-mix-legend" id="pm-ram-mix-legend">
        {parts.map((k) => {
          const v = on ? Number(mix![k]) || 0 : 0
          const pct = on && mix ? Math.round((v * 100) / mix.total) : 0
          const hide = !on || (v <= 0 && k !== 'free')
          return (
            <span key={k} className={`mx-${k}`} style={{ display: hide ? 'none' : undefined }}>
              <i></i>
              {k[0]!.toUpperCase() + k.slice(1)} <b>{on ? `${mix!.labels[k] || ''} (${pct}%)` : '—'}</b>
            </span>
          )
        })}
      </div>
    </div>
  )
}

function TrendCard({ id, metrics }: { id: number; metrics: { key: string; label: string }[] }) {
  const [metric, setMetric] = useState('cpu_util')
  const [range, setRange] = useState('24h')
  const { data, error } = useQuery(performanceTrendQuery(id, range, metric))
  return (
    <div className="pm-chart-card pm-trend" data-device={id} style={{ cursor: 'default' }}>
      <div className="pm-chart-head">
        <strong>Trend</strong>
        <span className="pm-trend-tabs" role="tablist">
          {metrics.map((m) => (
            <button
              key={m.key}
              type="button"
              className={`pm-trend-tab${m.key === metric ? ' is-on' : ''}`}
              data-metric={m.key}
              onClick={() => setMetric(m.key)}
            >
              {m.label}
            </button>
          ))}
        </span>
        <span className="pm-trend-ranges">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              className={`pm-trend-range${r.key === range ? ' is-on' : ''}`}
              data-range={r.key}
              onClick={() => setRange(r.key)}
            >
              {r.label}
            </button>
          ))}
        </span>
      </div>
      <TrendMeta data={data} error={!!error} />
      <div className="pm-chart-box pm-trend-box" data-chart="trend" style={{ height: 160 }}>
        <TrendLines data={data} />
      </div>
    </div>
  )
}

function TrendMeta({ data, error }: { data: PerformanceTrend | undefined; error: boolean }) {
  const stats = useMemo(() => {
    const points = data?.points ?? []
    if (!points.length) return null
    const lowest = data?.extreme === 'min'
    let peak = lowest ? Infinity : 0
    let sum = 0
    for (const p of points) {
      sum += Number(p.avg) || 0
      const v = Number(p.max)
      if (lowest ? v < peak : v > peak) peak = v
    }
    if (peak === Infinity) peak = 0
    return { avg: sum / points.length, peak, lowest, unit: data?.unit ?? '', tier: data?.tier ?? '' }
  }, [data])
  const fmt = (v: number, unit: string) => {
    const s = Math.abs(v) >= 100 || unit === '' ? String(Math.round(v)) : v.toFixed(1)
    return unit ? s + (unit === '%' ? '%' : ` ${unit}`) : s
  }
  if (error || !stats) {
    return (
      <div className="pm-trend-meta">
        <span className="pm-chart-now" data-trend="avg">
          —
        </span>
        <span className="pm-trend-peak">
          <span data-trend="extreme-label">peak</span> <b data-trend="max">—</b>
        </span>
        <span className="pm-trend-tier" data-trend="tier"></span>
      </div>
    )
  }
  return (
    <div className="pm-trend-meta">
      <span className="pm-chart-now" data-trend="avg" title={`average over the window · now ${fmt(stats.avg, stats.unit)}`}>
        {fmt(stats.avg, stats.unit)}
      </span>
      <span className="pm-trend-peak">
        <span data-trend="extreme-label">{stats.lowest ? 'lowest' : 'peak'}</span> <b data-trend="max">{fmt(stats.peak, stats.unit)}</b>
      </span>
      <span className="pm-trend-tier" data-trend="tier">
        {(TIER_NAME[stats.tier] || '').replace('peak', stats.lowest ? 'lowest' : 'peak')}
      </span>
    </div>
  )
}

function TrendLines({ data }: { data: PerformanceTrend | undefined }) {
  const points = data?.points ?? []
  if (!points.length) {
    return (
      <div className="alert alert-info" style={{ margin: 0, padding: '6px 10px', fontSize: 12 }}>
        <i className="fas fa-info-circle"></i> Nothing stored for this window yet - the history builds up as the machine reports.
      </div>
    )
  }
  return <Sparkline points={points.map((p) => ({ name: p.name, value: p.avg }))} peak={points.map((p) => p.max)} color="#36a3d9" height={160} />
}

function HistoryCard({ history }: { history: PerformanceHistory }) {
  return (
    <div className="pm-history">
      <div className="pm-history-head">
        <strong>Performance history</strong>
        <span>
          last {history.days} days &middot; average <em style={{ color: history.average_color, fontStyle: 'normal' }}>{history.average}</em> &middot; worst
          day <em style={{ color: history.worst_color, fontStyle: 'normal' }}>{history.worst}</em>
        </span>
      </div>
      <p className="pm-history-hint">
        100 is a day with no performance alerts. The score drops with every hour spent in alert, counted against an {history.work_hours}-hour working
        day.
      </p>
      <div className="pm-history-bars">
        {history.bars.map((bar) => (
          <div
            key={bar.day}
            className="pm-history-bar"
            style={{ height: bar.height, background: bar.color }}
            data-toggle="tooltip"
            data-placement="top"
            title={bar.title}
          ></div>
        ))}
      </div>
      <div className="pm-history-axis">
        <span>{history.from_label}</span>
        <span>today</span>
      </div>
    </div>
  )
}

function LineCard({
  chart,
  id,
  top,
  onOpen,
}: {
  chart: PerformanceChartMeta
  id: number
  top?: { name: string }
  onOpen: () => void
}) {
  const { data, error, isLoading } = useQuery(performanceSeriesQuery(id, chart.key as 'load' | 'memory' | 'apps'))
  const points = data?.points ?? []
  const last = points[points.length - 1]
  return (
    <div className="pm-chart-card" data-tab={chart.tab} onClick={onOpen}>
      <div className="pm-chart-head">
        <strong>{chart.title}</strong>
        <span className="pm-open">
          open <i className="fas fa-angle-right"></i>
        </span>
      </div>
      <div className="pm-top" data-top={chart.top}>
        {top?.name ? (
          <>
            Most consumption 24h: <strong>{top.name}</strong>
          </>
        ) : null}
      </div>
      <div className="pm-chart-now" data-now={chart.key}>
        {last ? formatNow(chart.kind, last.value) : '—'}
      </div>
      <div className="pm-chart-box" data-chart={chart.key}>
        {isLoading ? (
          <Spinner />
        ) : error ? (
          <div className="alert alert-danger" style={{ margin: 0, padding: '6px 10px', fontSize: 12 }}>
            <i className="fas fa-exclamation-circle"></i> Could not read this one.
          </div>
        ) : points.length === 0 ? (
          <div className="alert alert-info" style={{ margin: 0, padding: '6px 10px', fontSize: 12 }}>
            <i className="fas fa-info-circle"></i> Nothing to see yet...
          </div>
        ) : (
          <Sparkline points={points} color={chart.color} />
        )}
      </div>
    </div>
  )
}

function SplitCard({
  id,
  kind,
  title,
  color,
  nowA,
  nowB,
  labels,
  top,
  onOpen,
}: {
  id: number
  kind: 'discio' | 'network'
  title: string
  color: string
  nowA: string
  nowB: string
  labels: [string, string]
  top?: { name: string }
  onOpen: () => void
}) {
  const { data, error, isLoading } = useQuery(performanceSeriesQuery(id, kind))
  const split = data as PerformanceSplitSeries | undefined
  const summed = useMemo(() => sumSplit(split), [split])
  return (
    <div className="pm-chart-card" data-tab={kind === 'network' ? 'networkStatus' : 'discIO'} onClick={onOpen}>
      <div className="pm-chart-head">
        <strong>{title}</strong>
        <span className="pm-open">
          open <i className="fas fa-angle-right"></i>
        </span>
      </div>
      {top?.name ? (
        <div className="pm-top" data-top="io">
          Most consumption 24h: <strong>{top.name}</strong>
        </div>
      ) : kind === 'discio' ? (
        <div className="pm-top" data-top="io"></div>
      ) : null}
      <div className="pm-chart-now">
        <span data-now={nowA}>{summed ? `${formatBytes(summed.a)}/s` : '—'}</span>
        <span className="pm-open">{labels[0]}</span>
        <span data-now={nowB}>{summed ? `${formatBytes(summed.b)}/s` : '—'}</span>
        <span className="pm-open">{labels[1]}</span>
      </div>
      <div className="pm-chart-box" data-chart={kind}>
        {isLoading ? (
          <Spinner />
        ) : error ? (
          <div className="alert alert-danger" style={{ margin: 0, padding: '6px 10px', fontSize: 12 }}>
            <i className="fas fa-exclamation-circle"></i> Could not read this one.
          </div>
        ) : !summed || summed.points.length === 0 ? (
          <div className="alert alert-info" style={{ margin: 0, padding: '6px 10px', fontSize: 12 }}>
            <i className="fas fa-info-circle"></i> Nothing to see yet...
          </div>
        ) : (
          <Sparkline points={summed.points} color={color} />
        )}
      </div>
    </div>
  )
}

function sumSplit(split: PerformanceSplitSeries | undefined): { a: number; b: number; points: { name: string; value: number }[] } | null {
  if (!split || split.categories === undefined) return null
  const a: number[] = []
  const b: number[] = []
  for (const series of Object.values(split.dates || {})) {
    const first = series[0]?.data ?? []
    const second = series[1]?.data ?? []
    const n = Math.max(first.length, second.length, split.categories.length)
    for (let i = 0; i < n; i++) {
      a[i] = (a[i] || 0) + (Number(first[i]) || 0)
      b[i] = (b[i] || 0) + (Number(second[i]) || 0)
    }
  }
  if (!a.length && !b.length) return { a: 0, b: 0, points: [] }
  const points = []
  const len = Math.max(split.categories.length, a.length, b.length)
  for (let i = 0; i < len; i++) {
    points.push({ name: split.categories[i] || '', value: (a[i] || 0) + (b[i] || 0) })
  }
  return { a: a[a.length - 1] || 0, b: b[b.length - 1] || 0, points }
}

function Sparkline({
  points,
  peak,
  color,
  height = 118,
}: {
  points: { name: string; value: number }[]
  peak?: number[]
  color: string
  height?: number
}) {
  const width = 320
  const pad = 4
  const values = points.map((p) => p.value)
  const extras = peak ?? []
  const max = Math.max(1, ...values, ...extras)
  const step = points.length > 1 ? (width - pad * 2) / (points.length - 1) : 0
  const y = (v: number) => pad + (1 - v / max) * (height - pad * 2)
  const line = (nums: number[]) =>
    nums
      .map((v, i) => `${i === 0 ? 'M' : 'L'} ${(pad + i * step).toFixed(1)} ${y(v).toFixed(1)}`)
      .join(' ')
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} preserveAspectRatio="none">
      {peak && <path d={line(peak)} fill="none" stroke={color} strokeOpacity="0.35" strokeWidth="1.2" />}
      <path d={line(values)} fill="none" stroke={color} strokeWidth="2" />
    </svg>
  )
}
